import { memo, SVGProps, useMemo } from 'react';
import { Popover } from '.';
import { GradientType } from '../../modules/Properties/types';
import { Gradients } from '../../potree/materials/Gradients';

/** Runtime gradient stop with THREE.Color (from Potree.Gradients) */
type RuntimeGradientStop = [number, { toArray: () => number[] }];
type RuntimeGradientDefinition = RuntimeGradientStop[];

interface GradientProps extends SVGProps<SVGSVGElement> {
  colors: {
    color: number[];
    percent: string;
  }[];
  name: string;
}
const Gradient_: React.FC2<GradientProps> = ({ colors, name, ...props }) => {
  return (
    <svg width="1.5rem" height="1.5rem" {...props}>
      <defs>
        <linearGradient id={name} gradientTransform="rotate(90)">
          {colors.map(({ color, percent }, i) => (
            <stop key={i} offset={percent} stopColor={`rgb(${color[0]}, ${color[1]}, ${color[2]})`}></stop>
          ))}
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill={`url("#${name}")`}></rect>
    </svg>
  );
};
const Gradient = memo(Gradient_);

const convertGradient = (values: RuntimeGradientDefinition) =>
  values
    ?.map(([weight, color]: RuntimeGradientStop) => ({
      color: color.toArray().map((v: number) => v * 255),
      percent: `${100 - weight * 100}%`,
    }))
    .reverse() || [];

interface GradientPopoverProps {
  gradientValue: GradientType;
  onChange: (value: GradientType) => void;
  disabled?: boolean;
}
const GradientPopover_: React.FC2<GradientPopoverProps> = ({ gradientValue, onChange, disabled = false }) => {
  const buttonChild = useMemo(() => {
    const gradientValues = Gradients[gradientValue];
    const colors = convertGradient(gradientValues);

    return (
      <div className={`w-10 h-10 overflow-hidden border border-gray-500 rounded ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
        <Gradient name={'button-child-svg'} colors={colors} width="2.5rem" height="2.5rem" />
      </div>
    );
  }, [gradientValue, disabled]);

  if (disabled) {
    return buttonChild;
  }

  return (
    <Popover buttonChild={buttonChild} panelClassName="w-auto">
      {({ close }) => (
        <div className="grid w-46 grid-cols-5 gap-2">
          {(Object.entries(Gradients) as [GradientType, RuntimeGradientDefinition][]).map(([name, values]) => {
            const colors = convertGradient(values);
            return (
              <div
                className="w-6 h-6 overflow-hidden border border-gray-500 rounded cursor-pointer"
                key={name}
                onClick={() => {
                  close();
                  onChange(name);
                }}
              >
                <Gradient name={name} colors={colors} />
              </div>
            );
          })}
        </div>
      )}
    </Popover>
  );
};

export const GradientPopover = memo(GradientPopover_);
